import { useEffect, useState } from "react";
import { Share2, Copy, Check, Linkedin, Trophy, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import { BrandLockup } from "../BrandLogo";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { LINKEDIN_URL, SITE_URL, shareOnLinkedIn } from "../../lib/social";
import { getInvite, inviteLink } from "../../lib/invite";
import { useAuth } from "../../context/AuthContext";

const WINS = {
  score: {
    title: "Share your match score",
    line: "Tailored my CV to this role with GetInterviewed.",
    text: (s, url) => `I just tailored my CV for a new role and reached a ${s}% job match with GetInterviewed. It's free for job seekers in Singapore: ${url} #GetInterviewed #JobSearch #Singapore`,
  },
  interview: {
    title: "Share your interview news",
    line: "Landed an interview with help from GetInterviewed.",
    text: (s, url) => `Some good news: I've landed an interview! I prepared my application with GetInterviewed, which tailors your CV to each job. Free for job seekers in Singapore: ${url} #GetInterviewed #JobSearch`,
  },
  offer: {
    title: "Share your job offer",
    line: "Received a job offer after applying with GetInterviewed.",
    text: (s, url) => `I'm delighted to share that I've received a job offer! GetInterviewed helped me tailor my applications along the way. Free for job seekers in Singapore: ${url} #GetInterviewed #NewJob`,
  },
  profile: {
    title: "Share your profile milestone",
    line: "My career profile is complete and my CV is ready to go.",
    text: (s, url) => `My career profile is now 100% complete on GetInterviewed, and it wrote me a polished, keyword-rich CV from scratch. If you're job hunting, it's free for job seekers in Singapore: ${url} #GetInterviewed #JobSearch #Singapore`,
  },
};

function WinCard({ kind, score, line, label }) {
  return (
    <div className="rounded-2xl border border-brand/30 bg-gradient-to-br from-brand-soft to-surface p-6 text-center relative overflow-hidden" data-testid="share-win-card">
      <div className="absolute inset-0 af-grid opacity-20" />
      <div className="relative">
        <div className="inline-flex items-center mb-4"><BrandLockup size={28} textClass="text-base" /></div>
        {kind === "score" || kind === "profile" ? (
          <>
            <div className="font-display text-6xl font-black text-brand leading-none">{kind === "profile" ? 100 : score}<span className="text-2xl text-faint">%</span></div>
            <div className="text-xs font-semibold uppercase tracking-widest text-body mt-2">{kind === "profile" ? "Profile ready" : label}</div>
          </>
        ) : (
          <div className="flex flex-col items-center gap-2">
            {kind === "offer" ? <PartyPopper className="w-10 h-10 text-brand" /> : <Trophy className="w-10 h-10 text-brand" />}
            <div className="font-display text-3xl font-black text-ink">{kind === "offer" ? "Offer received" : "Interview landed"}</div>
          </div>
        )}
        <p className="mt-4 text-sm text-body">{line}</p>
      </div>
    </div>
  );
}

export default function ShareWinModal({ open, onOpenChange, score, kind = "score", label = "Job match" }) {
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);
  const [url, setUrl] = useState(SITE_URL);
  useEffect(() => {
    if (open && user) getInvite().then((d) => setUrl(inviteLink(d.code))).catch(() => {});
  }, [open, user]);
  const win = WINS[kind] || WINS.score;
  const text = win.text(score, url);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Copied. Paste it anywhere!");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Couldn't copy"); }
  };
  const linkedIn = async () => {
    const ok = await shareOnLinkedIn(text);
    if (ok) toast.success("Opening LinkedIn. Your post is also copied, in case it doesn't fill in.");
  };
  const nativeShare = async () => {
    if (!navigator.share) return copy();
    try { await navigator.share({ title: "GetInterviewed", text, url }); } catch { /* user closed the share sheet */ }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-line text-ink sm:max-w-md" data-testid="share-win-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2"><Share2 className="w-5 h-5 text-brand" /> {win.title}</DialogTitle>
          <DialogDescription className="text-body">Celebrate it with your network. No personal details from your CV are shared.</DialogDescription>
        </DialogHeader>
        <WinCard kind={kind} score={score} line={win.line} label={label} />
        <p className="text-sm text-body rounded-xl bg-canvas border border-line p-3 leading-relaxed" data-testid="share-win-text">{text}</p>
        <button data-testid="share-win-linkedin" onClick={linkedIn} className="w-full gi-btn gi-btn-primary">
          <Linkedin className="w-4 h-4" /> Post on LinkedIn
        </button>
        <div className="flex gap-2">
          <button data-testid="share-win-native" onClick={nativeShare} className="flex-1 gi-btn gi-btn-secondary"><Share2 className="w-4 h-4" /> More ways to share</button>
          <button data-testid="share-win-copy" onClick={copy} className="gi-btn gi-btn-secondary">
            {copied ? <Check className="w-4 h-4 text-brand" /> : <Copy className="w-4 h-4" />} {copied ? "Copied" : "Copy"}
          </button>
        </div>
        <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer" data-testid="share-win-follow" className="text-sm text-brand hover:underline text-center">
          Follow GetInterviewed on LinkedIn for job-search tips
        </a>
      </DialogContent>
    </Dialog>
  );
}
